/**
 * カード — 外交（がいこう）10枚
 *
 * docs/01-ゲームデザイン.md §9 の「外交 10枚」に対応する。
 *
 * 【設計方針】
 * 外交は「戦わずに済ませる技術」である。したがってこの10枚は、
 *   ・結ぶ（同盟・婚姻・養子縁組）
 *   ・担保する（人質・起請文）
 *   ・止める（和睦・惣無事）
 *   ・伝える（使僧・御内書）
 *   ・切る（手切れ）
 * という配分にしてある。合戦に触れる札は docs/06-賭けと軍配勝負.md の
 * ホールデムに噛み合う形（formationMod / holeCardMod）だけを使い、
 * 「サイコロ+1」のような旧仕様の効果は書かない。
 *
 * scope の読み方（core/effects.ts の Effect）:
 *   'attack' = 攻め手の役に作用  /  'defend' = 受け手の役に作用  /  'both' = 使用者の役に作用
 *
 * 人質（HITOJICHI）は docs/06 §7 の人質と同じ仕組み（手勢札の増減）で効く。
 * ルールの側にすでにある史実を、カードからも触れられるようにしたものである。
 *
 * 【執筆基準】docs/05-学習設計.md §4 と provinces/_reference.ts に揃える。
 *   - 武将を「英雄」「悪」と評価しない。戦闘行為を美化・賛美しない
 *   - 宗教勢力（一向一揆・キリスト教・比叡山）は事実の記述に留める
 *   - 女性・民衆の存在を消さない。婚姻・人質の当事者は多くが女性と子どもである
 *   - 通説と異説がある事項は caveat に必ず書く
 *   - ゲームの都合で史実を改変した箇所は gameNote に必ず書く
 *   - 年号は西暦を主とし、必要に応じて和暦を添える
 */

import type { Card } from '../types';

// ---------------------------------------------------------------------------
// 結ぶ
// ---------------------------------------------------------------------------

/** 同盟。甲相駿三国同盟を軸にするが、善得寺の会盟の創作性を caveat に落とさない。 */
export const DOUMEI: Card = {
  id: 'card:gaikou:doumei',
  name: '同盟',
  reading: 'どうめい',
  type: 'gaikou',
  cost: { zeni: 2 },
  timing: 'council',
  effects: [
    // 背後を気にせず一方面に力を向けられるようになる
    { t: 'gain', res: 'ishin', n: 2 },
    { t: 'gain', res: 'gunryaku', n: 1 },
  ],
  note: {
    summary:
      '大名どうしが互いに攻めないことを約束する取り決め。婚姻・人質・起請文で担保され、背後の安全を確保して他方面に兵を向けるために結ばれた。',
    detail: {
      1: '大名どうしが「おたがいに攻めない」と約束することです。約束を守るしるしに、むすめをおよめに出したり、子どもをあずけたりしました。背中の心配がなくなるので、べつの方向に兵を出せるようになります。武田・北条・今川の三つの家がむすんだ同盟が有名です。',
      2: '大名間で相互に不可侵と協力を約束する取り決め。1554年（天文23年）に成立した甲斐の武田晴信（信玄）・相模の北条氏康・駿河の今川義元による甲相駿三国同盟がよく知られる。三者は互いに娘を嫁がせ、武田義信に義元の娘、今川氏真に氏康の娘、北条氏政に信玄の娘が入るという三重の婚姻で結びを固めた。これにより武田は信濃、北条は関東、今川は西へと、それぞれ一方面に力を集められるようになった。同盟は1568年の武田氏の駿河侵攻で崩れ、翌1569年には北条氏と上杉氏が越相同盟を結んでいる。',
      3: '大名間で不可侵・協力を約する政治的結合。文書上は起請文の交換によって成立し、婚姻・人質・養子縁組といった人的な担保を伴うのが通例で、当事者だけで完結せず将軍・朝廷・寺社が仲介する場合も多かった。代表例の甲相駿三国同盟は、1552年から1554年（天文21〜23年）にかけて武田・北条・今川三家の間で段階的に結ばれた婚姻によって成立し、各家が北信濃・関東・東海というそれぞれの正面へ兵力を集中できる条件を作った。この同盟は1568年（永禄11年）の武田氏による駿河侵攻で破綻し、北条氏は敵対してきた上杉氏と越相同盟を結ぶ（1569年）という転換を選んでいる。同盟の実態は近代的な条約とは異なり、当事者の力関係が変われば容易に解消される流動的なもので、結ばれた期間の長短にも大きな差があった。婚姻で他家へ入った女性たちは、同盟が破れると実家へ送り返されたり、婚家に留まったまま消息を絶ったりしており、取り決めの帰結を最も直接に負ったのは彼女たちであった。',
    },
    year: [1554, 1569],
    keywords: ['同盟', '甲相駿三国同盟', '武田氏', '北条氏', '今川氏', '越相同盟'],
    caveat:
      '甲相駿三国同盟について「1554年に駿河の善得寺で三大名が一堂に会して盟約を結んだ」という話（善得寺の会盟）は、『甲陽軍鑑』など後世の軍記に見えるもので、同時代の史料では確認できません。三家の婚姻が段階的に結ばれたこと自体は事実ですが、三者会談の場面は史実とはいえないとするのが近年の一般的な見方です。',
    gameNote:
      '盤上では「威信+2・軍略+1」という自分だけへの効果にしています。実際の同盟は相手のある取り決めで、一方が得をして終わるものではありません。相手プレイヤーとの合意を必要としないのは、遊びやすさのための簡略化です。',
  },
};

/** 婚姻。女性を「道具」と書かず、当事者として書く（docs/05 §4-4）。 */
export const KONIN: Card = {
  id: 'card:gaikou:konin',
  name: '婚姻',
  reading: 'こんいん',
  type: 'gaikou',
  cost: { zeni: 3 },
  timing: 'council',
  effects: [
    // 家と家が結ばれ、化粧料として所領が動くこともあった
    { t: 'gain', res: 'ishin', n: 2 },
    { t: 'gain', res: 'koku', n: 1 },
  ],
  note: {
    summary:
      '家と家を結ぶための結婚。同盟の担保として広く行われた。嫁いだ女性は実家と婚家をつなぐ役割を担い、所領を持ち、文書を出して政務にあたった例もある。',
    detail: {
      1: '大名の家どうしが、むすめをおよめに出して結びつくことです。約束のしるしとして行われました。およめに行った女の人は、ただ送られただけではなく、二つの家をつなぐ大事な役目をはたし、なかには自分で領地をおさめた人もいました。',
      2: '家と家の結びつきを作るための結婚で、同盟の担保として広く行われた。織田信長の妹の市が近江の浅井長政に嫁いだ例、斎藤道三の娘（帰蝶、濃姫とも呼ばれる）が信長に嫁いだ例などが知られる。嫁いだ女性は化粧料（けしょうりょう）と呼ばれる所領を与えられることがあり、実家と婚家の間を取り持つ連絡役ともなった。今川氏親の妻の寿桂尼は、夫の死後に自分の印判を押した文書を出して今川家の政務にあたり、「女大名」と呼ばれることもある。婚姻は同盟が破れると解消され、女性は実家へ戻されることもあった。',
      3: '大名間・家臣間の政治的結合を目的とする婚姻。同盟の成立に際して起請文とともに交わされる担保の中心であり、甲相駿三国同盟のように複数家が相互に嫁がせ合う形も取られた。嫁いだ女性には化粧料として所領が付されることがあり、その所領の年貢や管理をめぐる文書が残る例もある。今川氏親の正室であった寿桂尼は、氏親の死後、子の氏輝が若年の間、「帰」の字を刻んだ自らの印判を用いて安堵状・諸役免許状などを発給し、領国支配の一端を担った。同時代に女性が印判状を発給した例は多くないため、しばしば「女戦国大名」と呼ばれる。織田信長の妹の市は浅井長政に嫁ぎ、浅井氏滅亡後に信長のもとへ戻り、のちに柴田勝家と再婚した。三人の娘（茶々・初・江）はいずれも後の政治史に関わっている。婚姻を「女性が政略の道具にされた」とのみ説明する理解は、近年の女性史研究では見直されており、婚家における財産管理・訴訟・人的仲介といった能動的な役割が具体的な史料から明らかにされつつある。',
    },
    year: [1500, 1600],
    keywords: ['婚姻', '政略結婚', '化粧料', '寿桂尼', '市', '女性の役割'],
    caveat:
      '市が浅井長政に嫁いだ年は永禄10年（1567年）とも11年（1568年）ともいわれ、確定していません。また「戦国の女性は政略結婚の道具にすぎなかった」という説明は近年見直されており、嫁ぎ先で所領を管理したり文書を発給したりした例が具体的に確認されています。',
    gameNote:
      '盤上では「威信+2・石高+1」としています。石高が増えるのは化粧料として所領が動いた事実を表した処理で、婚姻のたびに領国が広がったわけではありません。',
  },
};

/** 養子縁組。毛利両川と越相同盟の景虎。円満な縁組ばかりでない点を caveat に書く。 */
export const YOUSHI_ENGUMI: Card = {
  id: 'card:gaikou:youshi',
  name: '養子縁組',
  reading: 'ようしえんぐみ',
  type: 'gaikou',
  cost: { zeni: 2 },
  timing: 'council',
  effects: [
    // 他家の家臣団がそのまま味方になる
    { t: 'gain', res: 'hei', n: 2 },
    { t: 'gain', res: 'ishin', n: 1 },
  ],
  note: {
    summary:
      '自分の子を他家の跡継ぎに入れたり、他家の子を迎えたりして家を結ぶこと。婚姻と並ぶ結束の手段で、相手の家臣団ごと味方に取り込む効果があった。',
    detail: {
      1: '自分の子どもを、よその家のあとつぎとして送りこんだり、よその子をむかえたりすることです。家と家がつながるだけでなく、その家の家来たちもいっしょに味方になります。毛利元就が二人の子を吉川家と小早川家に入れたのが有名です。',
      2: '子を他家の家督継承者として入れる、または他家の子を迎える取り決め。安芸の毛利元就は、1544年（天文13年）に三男の徳寿丸（のちの小早川隆景）を竹原小早川家へ、1547年（天文16年）に次男の元春（のちの吉川元春）を吉川家へ入れた。両家は毛利氏を支える柱となり、後に毛利両川（もうりりょうせん）と呼ばれる。1569年（永禄12年）に上杉氏と北条氏が越相同盟を結んだ際にも、北条氏康の子が上杉謙信の養子となることが取り決められ、翌1570年に上杉景虎を名乗った。養子縁組は家の断絶を防ぐ手段でもあり、跡継ぎのない家に一族や他家から迎えることは広く行われた。',
      3: '他家の家督継承者に自家の子弟を送り込む、あるいは他家の子弟を迎えることで家と家を結合させる手段。婚姻と異なり、家督そのものを掌握できるため政治的効果が大きい。毛利元就は天文13年（1544年）に三男を竹原小早川家に入れ、のち沼田小早川家をも継がせて小早川隆景とし、天文16年（1547年）には次男を吉川興経の養子として吉川元春とした。両家は毛利本家を支える体制を形成し、後世に毛利両川と称される。越相同盟（1569年）では北条氏康の子三郎が上杉方へ送られ、上杉憲政の養子を経て1570年に謙信の養子となり、謙信がかつて名乗った景虎の名を与えられた。謙信の死後、1578年（天正6年）の御館の乱で上杉景勝と家督を争い、敗れて没している。養子縁組は家の存続策としても機能し、跡継ぎのない家が一族や姻戚から迎える事例は身分を問わず広く見られた。娘に婿を取って家を継がせる形も一般的で、家の継承は男系の実子相続だけで成り立っていたわけではない。',
    },
    year: [1544, 1578],
    keywords: ['養子縁組', '毛利両川', '吉川元春', '小早川隆景', '上杉景虎', '越相同盟'],
    caveat:
      '養子縁組は円満な合意ばかりではありません。吉川家への元春の入嗣は当主吉川興経を隠居させた上でのもので、興経は1550年（天文19年）に殺害されています。上杉景虎も、謙信の死後の家督争い（御館の乱）で敗れており、縁組が結束をもたらすとは限りませんでした。',
    gameNote:
      '盤上では「兵+2・威信+1」としています。兵が増えるのは、養子に入った先の家臣団が味方になることを表した処理で、養子縁組そのものが兵を生むわけではありません。',
  },
};

// ---------------------------------------------------------------------------
// 担保する
// ---------------------------------------------------------------------------

/** 人質。docs/06 §7 と同じ仕組み（手勢札の増減）で効かせる。 */
export const HITOJICHI: Card = {
  id: 'card:gaikou:hitojichi',
  name: '人質',
  reading: 'ひとじち',
  type: 'gaikou',
  cost: { zeni: 2 },
  timing: 'battle',
  effects: [
    // 相手の身内を預かっている。相手は思い切った手が打てない
    { t: 'target', who: 'chosen', then: [{ t: 'holeCardMod', n: -1 }] },
  ],
  note: {
    summary:
      '約束を守らせる担保として、子や妻、兄弟を相手方に預ける制度。「質（しち）」とも呼ばれ、預けられたのは多くが子どもと女性だった。',
    detail: {
      1: '約束をやぶらないというしるしに、自分の子どもや妻を相手の家にあずけることです。「質（しち）」ともいいます。あずけられたのは小さな子どもや女の人が多く、もし約束をやぶれば命があぶなくなることもありました。徳川家康も子どものころ、よその家であずけられて育ちました。',
      2: '同盟や服属の約束を守らせるための担保として、子・妻・兄弟などを相手方に預ける制度。「質」とも呼ばれた。松平竹千代（のちの徳川家康）は1547年（天文16年）に織田氏のもとへ送られ、1549年（天文18年）に人質交換で今川氏のもとへ移り、駿府で少年期を過ごした。人質を出す側にとっては裏切りを封じられることを意味し、預かる側にとっては相手を動かしにくくする手段だった。預けられたのは多くが子どもと女性で、当人には選ぶ余地がない立場だった。豊臣政権期には諸大名の妻子が京や大坂に置かれ、江戸幕府もこの仕組みを引き継いでいる。',
      3: '約諾の履行を担保するため、近親者を相手方へ差し出す慣行。史料上は「質」「証人」などと表記される。松平竹千代（徳川家康）は1547年（天文16年）に織田氏のもとへ、1549年（天文18年）に織田信広との交換によって今川氏のもとへ移された。1560年の桶狭間の戦い後、今川方に残っていた家康の妻子（築山殿と竹千代＝のちの信康）は、上ノ郷城で捕らえた鵜殿氏の子との交換によって取り戻されたと伝わる。人質は服属関係の可視化でもあり、大名は服属した国衆から証人を徴し、居城下に住まわせて統制した。豊臣政権は諸大名の妻子を大坂・京に集め、江戸幕府はこれを制度化して大名の妻子を江戸に置き、参勤交代とともに大名統制の柱とした。人質とされた者の多くは幼児・女性であり、彼らは政治的決裂の際に最初に危険にさらされる立場に置かれた。人質の処刑が実際にどの程度行われたかは史料に偏りがあり、軍記による誇張と実態の区別には注意を要する。',
    },
    year: [1547, 1600],
    keywords: ['人質', '質', '証人', '徳川家康', '駿府', '大名統制'],
    caveat:
      '徳川家康が今川氏のもとで不遇な少年期を送ったという語りは、後世の講談や物語で強調された部分が大きく、近年は今川一門に準じた待遇を受けていたとする見方も出されています。1547年に戸田康光の裏切りで織田方へ送られたという経緯にも異説があります。',
    gameNote:
      '盤上では「選んだ相手の手勢札を1枚減らす」としています。docs/06 §7 の人質と同じ仕組みで、思い切った手を打てなくなることを札の枚数に置き換えた処理です。人質にされた側の生死をゲームでは扱いません。',
  },
};

/** 起請文。神仏への誓約という事実の記述に留める（docs/05 §4-3）。 */
export const KISHOUMON_GAIKOU: Card = {
  id: 'card:gaikou:kishoumon',
  name: '起請文',
  reading: 'きしょうもん',
  type: 'gaikou',
  cost: { gunryaku: 1 },
  timing: 'council',
  effects: [
    // 誓約が文書として残り、約束の内容が動かせなくなる
    { t: 'gain', res: 'gunryaku', n: 2 },
    { t: 'gain', res: 'ishin', n: 1 },
  ],
  note: {
    summary:
      '神仏に誓う形式で書かれた誓約書。前半に誓う内容、後半に神仏の名を連ねて記し、熊野の護符（牛王宝印）の裏に書いて血判を据える例が多かった。',
    detail: {
      1: '「この約束をやぶったら、神さまや仏さまのばつを受けます」という形で書いた、ちかいの文書です。これを起請文（きしょうもん）といいます。熊野（いまの和歌山県）のお寺や神社がくばるお札のうらに書き、指を切って血で判をおすこともありました。大名どうしの約束や、家来が主人にちかうときに使われました。',
      2: '神仏に誓う形式をとった誓約書。前半（前書）に誓う内容を記し、後半（神文）に日本各地の神仏の名を書き連ねて、違反すれば神罰・仏罰を受けると記す構成をとる。鎌倉時代後期以降、寺社が配る護符である牛王宝印（ごおうほういん）の裏に書く習慣が広まり、とくに熊野三山の牛王宝印が用いられた。誓いを破れば熊野の神の使いである烏が死に、自分も地獄に落ちるとされ、血判を据えることもあった。戦国大名は同盟や和睦の際に起請文を交換し、家臣に主人への忠誠を誓わせる際にも用いた。信濃の生島足島神社には、武田氏の家臣が提出した起請文が多数伝わっている。',
      3: '神仏への誓約の形式をとる証文。構成は前書（誓約の内容）と神文（罰を下す神仏の列挙、「梵天帝釈四大天王、惣じて日本国中六十余州の大小神祇」といった定型を含む）からなり、末尾に署名と血判を加える例が多い。鎌倉後期以降、寺社が頒布する牛王宝印の裏面を用紙とする慣行が定着し、なかでも熊野三山の牛王宝印（熊野牛王符）が最も広く使われたため、起請文は「熊野誓紙」とも呼ばれた。熊野の神使である烏が図案化されており、誓いを破れば烏が死に、自身も無間地獄に堕ちると観念された。用途は大名間の同盟・和睦の担保、家臣の忠誠の確認、村落間の相論の和与、訴訟における当事者の陳述の裏付けなど広範に及ぶ。信濃の生島足島神社には永禄年間を中心とする武田氏関係の起請文が約80通伝存し、家臣一人ひとりの署名と血判を見ることができる。もっとも、起請文が交わされた同盟がその後破棄された例は多く、誓約文書の存在がそのまま履行を保証したわけではない。誓約と違背の双方が同時代の常態であったことを、史料は示している。',
    },
    year: [1300, 1600],
    keywords: ['起請文', '熊野牛王宝印', '神文', '血判', '生島足島神社', '誓約'],
    caveat:
      '起請文は神仏への誓いという形式を持ちますが、交わされた約束が守られたとは限りません。起請文を交換した同盟が数年で破られた例は多く、「神罰を恐れて約束が守られた」と単純に説明することはできません。',
    gameNote:
      '盤上では「軍略+2・威信+1」としています。軍略が増えるのは、約束の内容が文書として固定され、交渉の見通しが立ちやすくなることを表した処理です。',
  },
};

// ---------------------------------------------------------------------------
// 止める
// ---------------------------------------------------------------------------

/** 和睦。合戦を打ち切る札。Effect 型で表せる範囲に落として gameNote で補う。 */
export const WABOKU: Card = {
  id: 'card:gaikou:waboku',
  name: '和睦',
  reading: 'わぼく',
  type: 'gaikou',
  cost: { zeni: 2 },
  timing: 'reaction',
  effects: [
    // 兵を退いて陣を解く。戦わずに済ませる代わりに、面目は立たない
    { t: 'gain', res: 'hei', n: 3 },
    { t: 'gain', res: 'koku', n: 1 },
    { t: 'lose', res: 'ishin', n: 1 },
  ],
  note: {
    summary:
      '戦っている者どうしが争いをやめる取り決め。当事者だけでなく、将軍・朝廷・寺社・近隣の大名が仲立ちに入ることが多く、起請文や人質で担保された。',
    detail: {
      1: 'たたかいをやめて、なかなおりすることです。当人どうしだけで決めることもありますが、将軍や天皇、お寺などが間に入って「やめなさい」と言うこともありました。約束のしるしに、神仏にちかった文書（起請文・きしょうもん）をとりかわしたり、人質を出したりしました。',
      2: '交戦している勢力が戦いを終える取り決め。当事者間の交渉で成立することもあれば、第三者の仲介によることもあった。室町幕府の将軍足利義輝は各地の大名の争いにたびたび和睦を勧めており、1570年（元亀元年）の織田氏と浅井・朝倉氏の戦いでは正親町天皇と将軍足利義昭の働きかけで戦いが止められた。1580年（天正8年）の織田氏と石山本願寺の和睦も天皇の勅命による講和として成立している。1582年（天正10年）の備中高松城では、羽柴秀吉と毛利氏の間で城主の切腹と引き換えに和睦が結ばれた。和睦は起請文の交換や人質の授受で担保されたが、数年で破られることも珍しくなかった。',
      3: '交戦状態の終結を取り決める行為。同時代の文書では「無事（ぶじ）」の語がしばしば和平の意味で用いられる。成立の経路は、当事者間の直接交渉、第三者の仲介、上位権力の命令の三通りに大別できる。仲介者には将軍・朝廷・有力寺社・近隣大名・連歌師や商人といった人的ネットワークが含まれた。足利義輝は将軍権力の実質が細る中で、各地の大名間の和睦調停を通じて権威を保とうとしている。1570年の織田氏と浅井・朝倉氏の対陣は朝廷と将軍の働きかけで一旦収められ、1580年の石山本願寺と織田氏の講和は正親町天皇の勅命という形式をとった。1582年の備中高松城では、城主清水宗治の切腹を条件に羽柴秀吉と毛利氏の和睦が成立し、秀吉は本能寺の変の報を得て京へ引き返している。和睦の履行は起請文と人質によって担保されたが、力関係の変化に応じて破棄されることも多く、恒久的な平和をもたらす制度ではなかった。「〇〇の戦いで決着がついた」という説明は結果から遡った見方であり、当時の当事者にとって和睦はしばしば次の戦いまでの一時停止でもあった。',
    },
    year: [1550, 1600],
    keywords: ['和睦', '無事', '講和', '足利義輝', '勅命講和', '備中高松城'],
    caveat:
      '和睦は恒久的な平和を意味しませんでした。起請文や人質で担保されても数年で破られる例が多く、和睦の成立をもって争いが終わったと説明するのは正確ではありません。仲介者が「公平な第三者」であったとも限らず、自らの利害から介入した例も多くあります。',
    gameNote:
      '盤上では「兵+3・石高+1・威信-1」という加算で表していますが、実際の和睦は資源を生む取り決めではありません。合戦を打ち切って兵の損失を避けることの利を、遊びの上で加算に置き換えた処理です。威信が下がるのは、戦わずに退いたことへの家中や周囲の見方を表しています。',
  },
};

/** 惣無事。「惣無事令」という法令の存在自体に議論がある点を caveat の中心に置く。 */
export const SOUBUJI: Card = {
  id: 'card:gaikou:soubuji',
  name: '惣無事',
  reading: 'そうぶじ',
  type: 'gaikou',
  cost: { zeni: 2, gunryaku: 2 },
  timing: 'council',
  availableFrom: 4,
  effects: [
    // 私戦を止めさせる立場に立つ。他家は動きを縛られる
    { t: 'gain', res: 'ishin', n: 2 },
    { t: 'target', who: 'others', then: [{ t: 'lose', res: 'gunryaku', n: 1 }] },
  ],
  note: {
    summary:
      '大名どうしが勝手に戦うことを禁じ、争いは上位の権力の裁定に委ねさせる方針。豊臣秀吉が関東・奥羽や九州に向けて示したものが知られる。',
    detail: {
      1: '「大名どうしで勝手にたたかってはいけない。もめごとは上に訴えて決めてもらいなさい」というきまりです。豊臣秀吉が全国の大名に向けて示しました。これを守らなかったことを理由に、攻められた大名もいます。',
      2: '大名間の私的な戦いを禁じ、領地をめぐる争いは上位権力の裁定に従わせるとする方針。豊臣秀吉は1585年（天正13年）に関東・奥羽の大名へ、1587年（天正15年）に九州の大名へ、戦いをやめるよう求めている。1589年（天正17年）には、北条氏の家臣が真田氏の名胡桃城を奪い、これがこの方針に反するとされて、翌1590年（天正18年）に秀吉が小田原へ兵を進める名分となった。ただし私的な戦いを禁じる命令は秀吉が初めて出したものではなく、室町幕府の将軍や織田信長も同様の命令を出している。',
      3: '大名間の私戦を停止させ、境目相論を上位権力の裁定に委ねさせる政策方針。豊臣秀吉は天正13年（1585年）に関東・奥両国、天正15年（1587年）に九州へ向けて戦闘停止を求める意思を示し、天正17年（1589年）の名胡桃城事件を北条氏による違背として、翌年の小田原攻めの名分とした。この一連の政策は、歴史学者の藤木久志によって「惣無事令」という法令として体系的に位置づけられ、豊臣平和令論として長く通説的な地位を占めた。しかし近年、竹井英文らによって「惣無事令」という単一の法令の存在は史料上確認できないこと、「惣無事」は当時、和平一般を指す普通の語として広く用いられていたこと、私戦禁止の命令自体は室町将軍や織田信長にも見られ豊臣政権に固有ではないことが指摘され、通説は大きく見直されている。したがって現在は、「惣無事令」を条文をもつ法令として説明するのではなく、豊臣政権が各地の紛争に介入する際に用いた論理として理解する見方が有力である。この方針は在地の側からは、村や国衆どうしの自力による解決を封じる圧力としても働いた。',
    },
    year: [1585, 1590],
    keywords: ['惣無事', '豊臣秀吉', '私戦禁止', '名胡桃城事件', '小田原攻め', '藤木久志'],
    caveat:
      '「惣無事令」という名前の法令が実際に発布されたかどうかは、近年の研究で疑問視されています。この呼び名は歴史学者の藤木久志による整理に由来するもので、竹井英文らは、「惣無事」が当時の和平一般を指す普通の語であったこと、私戦を禁じる命令は秀吉以前にも将軍や織田信長が出していたことを指摘しています。教科書の記述もこの議論を受けて変化しつつあります。',
    gameNote:
      '盤上では「威信+2・他のプレイヤーの軍略-1」としています。実際の惣無事は自分だけが有利になる札ではなく、上位権力の側に立って介入する論理でした。他家の軍略を減らす処理は、その圧力を遊びに落とし込んだものです。',
  },
};

// ---------------------------------------------------------------------------
// 伝える
// ---------------------------------------------------------------------------

/** 使僧。外交文書を書いたのは五山の禅僧だったという事実を出す札。 */
export const SHISOU: Card = {
  id: 'card:gaikou:shisou',
  name: '使僧',
  reading: 'しそう',
  type: 'gaikou',
  cost: { zeni: 1 },
  timing: 'anytime',
  effects: [
    // 僧が国境を越えて往来し、文書と情勢がもたらされる
    { t: 'draw', n: 1 },
    { t: 'gain', res: 'gunryaku', n: 1 },
  ],
  note: {
    summary:
      '大名の使者を務めた僧。漢文の外交文書を書ける学識があり、宗派の縁で国境を越えて往来できたため、交渉役として重用された。',
    detail: {
      1: 'お坊さんが大名の使いとして、よその国へ交渉に行くことです。お坊さんはむずかしい漢文が書けて、お寺のつながりでどこの国にも行きやすかったので、使いの役目にえらばれました。外国とのやりとりの文書も、お坊さんが書いていました。',
      2: '大名の使者を務めた僧侶。五山（京都・鎌倉の有力な禅寺）の僧は漢文の素養が高く、明との外交文書の起草を担ったため、外交の実務者として重んじられた。天龍寺妙智院の策彦周良（1501〜1579）は大内義隆の要請を受け、1539年（天文8年）に遣明船の副使、1547年（天文16年）に正使として明へ渡り、『初渡集』『再渡集』という記録を残した。安芸の安国寺恵瓊は毛利氏の使僧として羽柴秀吉らとの交渉にあたっている。僧のほか、連歌師・商人・医師なども国境を越えて往来し、使者や情報の運び手となった。',
      3: '大名の外交交渉を担った僧侶。五山禅林の僧は漢籍と漢文作成の素養を持ち、室町幕府の対明外交では国書の起草・応接を担当したため、その学識と寺院間ネットワークが大名外交にも用いられた。天龍寺塔頭妙智院の策彦周良（1501〜1579）は、大内義隆の推挙により天文8年（1539年）の遣明船に副使として、天文16年（1547年）には正使として渡航し、道中と明での見聞を『初渡集』『再渡集』に詳細に記した。これらは日明関係史の基本史料となっている。安芸の安国寺恵瓊は毛利氏の使僧として織田・豊臣方との折衝にあたり、天正元年（1573年）の書状で織田政権の行く末と羽柴秀吉の力量に触れた一節がよく引かれる。ただしこれは毛利氏家中へ宛てた情勢報告の一部であり、「予言」として読むのは後世の読み方である。僧以外にも、連歌師・商人・医師・山伏といった、身分と国境を越えて移動できる人々が使者や情報伝達の役を果たしており、戦国期の外交は職業的な外交官ではなくこうした人的ネットワークによって支えられていた。',
    },
    year: [1500, 1600],
    keywords: ['使僧', '五山', '策彦周良', '遣明船', '安国寺恵瓊', '連歌師'],
    caveat:
      '安国寺恵瓊が1573年の書状で織田信長の政権の行く末や羽柴秀吉について記した一節は「予言」として紹介されることがありますが、実際には毛利氏に宛てた情勢分析の文書の一部です。結果を知ってから読み返した評価であることに注意が要ります。',
    gameNote:
      '盤上では「カードを1枚引く・軍略+1」としています。カードを引くのは、僧が国境を越えて情報と文書を運んだことを表した処理です。',
  },
};

/** 御内書。将軍の実力が細っても文書の力が残った、という事実を扱う。 */
export const GONAISHO: Card = {
  id: 'card:gaikou:gonaisho',
  name: '御内書',
  reading: 'ごないしょ',
  type: 'gaikou',
  cost: { zeni: 2 },
  timing: 'council',
  effects: [
    // 将軍の呼びかけが諸国に届き、話が集まってくる
    { t: 'gain', res: 'ishin', n: 1 },
    { t: 'draw', n: 2 },
  ],
  note: {
    summary:
      '室町幕府の将軍が出した書状形式の文書。命令書ではなく私信の形をとるが、将軍の意思を伝えるものとして重んじられ、和睦の勧めや上洛の要請に用いられた。',
    detail: {
      1: '将軍が大名に出した手紙のかたちの文書です。命令書ではなく手紙の形ですが、将軍の考えを伝えるものとして大事にされました。「けんかをやめなさい」「京へ来て助けてほしい」といったことが書かれ、各地の大名に届けられました。',
      2: '室町幕府の将軍が発給した書状形式の文書。正式な命令書（下文・御教書）ではなく私信の体裁をとるが、将軍の意思を直接伝えるものとして重んじられた。13代将軍足利義輝は各地の大名の争いに和睦を勧める御内書をさかんに出し、15代将軍足利義昭は上洛の支援を求める御内書を諸国に送っている。義昭は織田信長と対立した後も御内書を各地へ発し、武田・朝倉・浅井・毛利や本願寺などに呼びかけて信長に対抗する動きをつくった。信長は1569年（永禄12年）以降の掟書で、義昭が諸国へ御内書を出す際に自分の副状を添えることを定め、将軍の文書発給に枠をはめようとしている。',
      3: '室町将軍が発給した書状形式の文書。下文・御教書のような公式の様式ではなく私的な書状の体裁をとるが、将軍の意思を直接示すものとして受給者に重んじられ、御礼の進物を伴うことも多かった。応仁の乱後、幕府の実効支配が畿内周辺に縮小しても、御内書は将軍権威を諸国へ及ぼす主要な手段であり続けた。13代将軍足利義輝は各地の大名間の抗争に和睦を勧める御内書を多数発し、調停者としての立場から将軍権威の回復を図っている。15代将軍足利義昭は、越前・近江を流浪した時期に上洛支援を求める御内書を諸国へ送り、織田信長と決裂した後も各地の大名や寺院へ呼びかけを続けた。これに対し信長は、永禄12年（1569年）の殿中御掟とその後の条書によって、義昭の御内書発給に信長の副状を要することなどを定め、将軍の文書行為を統制しようとした。義昭は1573年に京を追われた後も将軍職を保持し、備後の鞆から御内書を発し続けている。実力を失った権威が、文書という形式を通じてなお政治的に機能した事例として重要である。',
    },
    year: [1550, 1590],
    keywords: ['御内書', '室町幕府', '足利義輝', '足利義昭', '殿中御掟', '将軍権威'],
    caveat:
      '御内書を受け取った大名が必ず従ったわけではありません。足利義昭の呼びかけに応じるかどうかは各大名の判断で、無視された例も多くあります。「将軍の権威は名ばかりだった」という説明も、逆に「御内書一つで大名が動いた」という説明も、どちらも一面的です。',
    gameNote:
      '盤上では「威信+1・カードを2枚引く」としています。カードを引くのは、御内書が各地へ飛んで話が集まってくることを表した処理で、将軍の文書が資源を生んだという意味ではありません。',
  },
};

// ---------------------------------------------------------------------------
// 切る
// ---------------------------------------------------------------------------

/** 手切れ。同盟の破棄。攻め手の役を上げる代わりに威信を大きく失う。 */
export const TEGIRE: Card = {
  id: 'card:gaikou:tegire',
  name: '手切れ',
  reading: 'てぎれ',
  type: 'gaikou',
  cost: { gunryaku: 2 },
  timing: 'battle',
  effects: [
    // 備えのない相手に向かうことになる
    { t: 'holeCardMod', n: 1, scope: 'attack' },
    // 盟約を破ったことは諸国に伝わる
    { t: 'lose', res: 'ishin', n: 2 },
  ],
  note: {
    summary:
      '結んでいた同盟を破棄すること。1568年の武田氏による駿河侵攻で甲相駿三国同盟が崩れた例が知られる。破棄した側は諸国からの信を失った。',
    detail: {
      1: 'むすんでいた同盟をやめて、相手を攻めることです。武田信玄は今川・北条と同盟をむすんでいましたが、1568年に今川の駿河へ攻めこみ、同盟はこわれました。約束をやぶった家は、ほかの家からも信用されにくくなります。',
      2: '結んでいた同盟を破棄し、相手方へ兵を向けること。甲斐の武田氏は1554年から今川氏・北条氏と同盟を保っていたが、1568年（永禄11年）に駿河へ侵攻し、甲相駿三国同盟は崩れた。これに反発した北条氏は今川氏を支援し、長年敵対してきた越後の上杉氏と越相同盟を結ぶ（1569年）。武田氏の側では、これに先立つ1565年（永禄8年）に嫡男の義信が廃嫡・幽閉され、1567年に死去している（義信事件）。義信の妻は今川義元の娘で、事件の後に今川氏のもとへ返された。同盟の破棄は、当事者の家族の立場を直接に左右した。',
      3: '同盟関係の一方的な破棄。永禄11年（1568年）、武田信玄は駿河へ侵攻し、天文23年（1554年）以来の甲相駿三国同盟は解消された。桶狭間の戦い（1560年）後の今川氏の弱体化と、武田氏にとっての海への出口の確保が背景として指摘される。北条氏康はこれに強く反発して今川氏を支援し、永禄12年（1569年）には長年の敵対関係にあった上杉謙信と越相同盟を結ぶという転換を行った。武田氏の家中では、これに先立つ永禄8年（1565年）に嫡男義信が廃嫡され、東光寺に幽閉されたのち永禄10年（1567年）に没している（義信事件）。義信の正室であった今川義元の娘（嶺松院）は、同盟の破綻に伴って今川方へ送り返された。手切れは軍事的な選択であると同時に、婚姻・人質・起請文で編まれた人的な網を断ち切る行為であり、その帰結を最も直接に負ったのは、両家の間に置かれていた女性と子どもたちであった。約束を破った家は他家からの信を失い、以後の交渉で不利な立場に立たされることにもなった。',
    },
    year: [1565, 1569],
    keywords: ['手切れ', '同盟破棄', '駿河侵攻', '義信事件', '甲相駿三国同盟', '越相同盟'],
    caveat:
      '義信事件の原因を「今川との同盟の継続をめぐる父子の対立」と説明することが広く行われていますが、これは『甲陽軍鑑』などの後世の記録に依るところが大きく、確実な同時代史料は乏しいのが実情です。武田氏が駿河へ侵攻した理由も、海への出口の確保、今川氏の弱体化、上杉氏との関係など複数の要因が指摘されており、一つに絞ることはできません。',
    gameNote:
      '盤上では「攻め手の役が1段階上がる・威信-2」としています。役が上がるのは備えのない相手に向かうことを表した処理で、盟約を破る行為を有利なものとして推奨する意図はありません。威信を大きく失うのは、諸国からの信を損なう代償を数値にしたものです。',
  },
};

// ---------------------------------------------------------------------------
// まとめ
// ---------------------------------------------------------------------------

/** 外交カード 10枚。宿場・湊で購入し、評定で引く。 */
export const GAIKOU_CARDS: readonly Card[] = [
  // 結ぶ
  DOUMEI,
  KONIN,
  YOUSHI_ENGUMI,
  // 担保する
  HITOJICHI,
  KISHOUMON_GAIKOU,
  // 止める
  WABOKU,
  SOUBUJI,
  // 伝える
  SHISOU,
  GONAISHO,
  // 切る
  TEGIRE,
];
